import styled, { css } from 'styled-components';

import { device } from '@/styles/helpers';

export interface ScBannerTemplateProps {
  backgroundImageUrl: string;
  backgroundMobUrl?: string;
  flyingImageConfig?: {
    posX: string;
    posY: string;
    posXMob: string;
    posYMob: string;
    sizeMob?: string;
    size?: string;
  };
}

export const ScBannerTemplate = styled.div<ScBannerTemplateProps>`
  background: url(${({ backgroundImageUrl }) => backgroundImageUrl})
    center/cover no-repeat fixed;
  position: relative;
  min-height: 100vh;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  overflow: hidden;

  ${({ backgroundMobUrl }) =>
    backgroundMobUrl &&
    css`
      @media (max-width: 530px) {
        background-image: url(${backgroundMobUrl});
        background-position: top left;
      }
    `}

  & .black-friday {
    margin-top: -20px;
    font-size: 14px;
  }

  & .banner__image {
    position: absolute;
    width: 100%;
    right: 0;
    display: flex;
    align-items: end;
    justify-content: space-between;

    @media (${device.mobile}) {
      padding: 0 16px 16px;
    }

    & > div {
      width: 100%;
    }

    & img {
      width: max-content;
      max-width: 400px;
      position: relative;
      right: 0;
      top: 0;
      height: fit-content;

      &.desktop {
        display: block;
      }

      &.mobile {
        display: none;
      }
    }
  }

  & .bgImg {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 55%;

    & .heroImg {
      width: 100%;
    }

    & .flying {
      width: ${({ flyingImageConfig }) =>
        flyingImageConfig?.size ?? 'fit-content'};
      position: absolute;
      left: ${({ flyingImageConfig }) => flyingImageConfig?.posX ?? 0};
      top: ${({ flyingImageConfig }) => flyingImageConfig?.posY ?? '10%'};
      animation: flying 2s 0s ease-in-out infinite alternate;
      aspect-ratio: 1 / 1;
    }
  }

  & .hero {
    align-self: center;
    display: flex;
    max-width: 50%;
    width: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    text-transform: uppercase;
    font-weight: 700;
    gap: 32px;
    position: relative;
    z-index: 999;
    padding: 4% 0;

    & .button-terms-box {
      display: grid;
      gap: 16px;
      justify-items: center;
    }

    &.imageHidden {
      max-width: 100%;
      padding: 32px 0;
      gap: 32px;

      & .hero__prizes {
        max-width: 500px;
      }

      @media (max-width: 768px) {
        padding: 16px;
      }
    }

    & .hero__prizes {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      max-width: 400px;

      & img {
        width: fit-content;
      }
    }

    & .hero__recommended {
      margin-bottom: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }

    &.withLeftImage {
      max-width: 100%;
      padding: 177px 0;
    }
    &.leftImgWithoutPadding {
      max-width: 100%;
    }

    & > .button-terms-box {
      grid-template-columns: minmax(320px, 1fr);

      & button {
        width: min(320px, 100%);
        margin-bottom: 0;
        @media (${device.mobileS}) {
          width: 100%;
        }
      }
    }

    & .hero__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      @media (${device.mobile}) {
        gap: 8px;
      }
    }

    & .hero__welcome {
      color: ${({ theme }) => theme.yellowColor};
    }

    & .hero__welcome,
    & .description {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: 0.44px;
    }

    & .welcome-bonus {
      font-size: 56px;
      line-height: 72px;
      letter-spacing: 1.68px;
      @media (${device.mobile}) {
        font-size: 40px;
        line-height: 56px;
        letter-spacing: 1.2px;
      }
    }

    & .hero__text {
      font-size: 32px;
      line-height: 40px;
      letter-spacing: 0.03em;
      max-width: 432px;

      & + .hero__welcome {
        margin-bottom: 0;
      }
    }

    & .hero__code {
      & > div {
        margin-bottom: 0;

        & > span {
          margin-left: 1rem;
          line-height: 40px;
        }
      }
    }
  }

  @media (max-width: 1120px) {
    & .bgImg {
      & .flying {
        left: 0%;
        top: -20%;
      }
    }
  }

  @media (${device.mobile}) {
    flex-direction: column;

    & .black-friday {
      margin-top: 0;
      font-size: 14px;
    }

    & .hero {
      max-width: 100%;
      justify-content: flex-start;
      padding: 1rem;
      gap: 32px;

      &.withLeftImage {
        padding: 16px;
      }
    }

    & .bgImg {
      width: 80%;
      position: relative;

      & .flying {
        left: -25%;
        top: 20%;
        max-width: 231px;
      }
    }
    & .banner__image {
      width: 100%;
      height: auto;
      position: relative;

      & > div {
        display: none;
      }

      & img {
        max-width: none;
        margin-top: 1rem;
        width: 100%;
        height: auto;
        &.desktop {
          display: none;
        }

        &.mobile {
          overflow: auto;
          display: block;
        }
      }
    }
  }

  @media (max-width: 690px) {
    .hero {
      & .hero__welcome {
        font-size: 20px;
        line-height: 26px;
      }

      & .hero__text {
        font-size: 28px;
        line-height: 36px;
      }
    }
  }

  @media (max-width: 530px) {
    & .bgImg {
      width: 100%;

      & .flying {
        width: ${({ flyingImageConfig }) =>
          flyingImageConfig?.sizeMob ?? '140px'};
        left: ${({ flyingImageConfig }) =>
          flyingImageConfig?.posXMob ?? '-15%'};
        top: ${({ flyingImageConfig }) => flyingImageConfig?.posYMob ?? '5%'};
      }
    }

    & .hero a button {
      max-width: none;
    }
  }

  @media (max-width: 420px) and (max-height: 690px) {
    & .bgImg {
      & img.flying {
        top: 15%;
        left: -75%;
        max-width: 150px;
      }
    }
  }
  @media (max-width: 420px) and (max-height: 800px) {
    & .hero {
      gap: 16px;
      & .hero__code > div {
        line-height: normal !important;
      }

      & .hero__text {
        font-size: 22px;
      }
    }
  }
  @media (max-width: 768px) and (max-height: 690px) {
    & .bgImg {
      width: 60%;
      max-width: 250px;

      & .flying {
        left: -65%;
        top: 10%;
        max-width: 231px;
      }
    }
  }

  @keyframes flying {
    0% {
      -webkit-transform: translateY(0);
      transform: translateY(0);
    }

    100% {
      -webkit-transform: translateY(-20px);
      transform: translateY(-20px);
    }
  }
`;
